import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  InputAdornment,
  TextField,
  FormControlLabel,
  Checkbox,
  Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { apiService } from '../services/api';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';

const ProductComparison = () => {
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(30, 'day'));
  const [dateTo, setDateTo] = useState(dayjs());
  const [compareDateFrom, setCompareDateFrom] = useState(dayjs().subtract(60, 'day'));
  const [compareDateTo, setCompareDateTo] = useState(dayjs().subtract(30, 'day'));
  const [productComparison, setProductComparison] = useState([]);
  const [productSales, setProductSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [limit, setLimit] = useState(50);
  const [compareEnabled, setCompareEnabled] = useState(true);

  useEffect(() => {
    loadProductComparison(false); // Initial load is comparison, not refresh
  }, []);

  const loadProductComparison = async (isRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const fromDate = dateFrom.format('YYYY-MM-DD');
      const toDate = dateTo.format('YYYY-MM-DD');

      if (compareEnabled) {
        // Comparison mode - load comparison data
        const compareFromDate = compareDateFrom.format('YYYY-MM-DD');
        const compareToDate = compareDateTo.format('YYYY-MM-DD');

        console.log(isRefresh ? '🔄 Refreshing product comparison...' : '📊 Loading new product comparison...', {
          current: { from: fromDate, to: toDate },
          compare: { from: compareFromDate, to: compareToDate }
        });

        const response = await apiService.getProductComparison({
          date_from: fromDate,
          date_to: toDate,
          compare_date_from: compareFromDate,
          compare_date_to: compareToDate,
          limit: limit
        });

        setProductComparison(response.data || []);
        setProductSales([]);
        console.log(isRefresh ? '🔄 Product comparison refresh completed:' : '✅ Product comparison loaded:', response.data?.length, 'products');

      } else {
        // Single period mode - load product sales only
        console.log(isRefresh ? '🔄 Refreshing product sales...' : '📦 Loading product sales...', {
          from: fromDate, to: toDate
        });

        const response = await apiService.getProductSales({
          date_from: fromDate,
          date_to: toDate
        });

        setProductSales(response.data || []);
        setProductComparison([]);
        console.log(isRefresh ? '🔄 Product sales refresh completed:' : '✅ Product sales loaded:', response.data?.length, 'products');
      }

    } catch (err) {
      console.error('❌ Product data loading error:', err);
      setError(`Fout bij laden van product gegevens: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Separate function for refresh action
  const refreshProductComparison = () => {
    loadProductComparison(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined || isNaN(percentage)) return 'N/A';
    const sign = percentage > 0 ? '+' : '';
    return `${sign}${percentage.toFixed(1)}%`;
  };

  const getPercentageColor = (percentage) => {
    if (percentage === null || percentage === undefined || isNaN(percentage)) return 'default';
    if (percentage > 0) return 'success';
    if (percentage < 0) return 'error';
    return 'default';
  };

  const getPercentageIcon = (percentage) => {
    if (percentage === null || percentage === undefined || isNaN(percentage)) return null;
    if (percentage > 0) return <TrendingUpIcon fontSize="small" />;
    if (percentage < 0) return <TrendingDownIcon fontSize="small" />;
    return null;
  };

  // Filter products based on search
  const filteredProducts = compareEnabled 
    ? productComparison.filter(product =>
        product.productName.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (product.sku && product.sku.toLowerCase().includes(searchFilter.toLowerCase()))
      )
    : productSales.filter(product =>
        product.productName.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (product.sku && product.sku.toLowerCase().includes(searchFilter.toLowerCase()))
      );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Analytics wordt geladen...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Analytics & Trends
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" paragraph>
        {compareEnabled 
          ? 'Vergelijk productprestaties tussen twee verschillende periodes'
          : 'Bekijk productprestaties voor een specifieke periode'
        }
      </Typography>

      {/* Date Range Selectors */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Periode Selectie
        </Typography>
        
        {/* Compare Toggle */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={compareEnabled}
                onChange={(e) => setCompareEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Vergelijk met andere periode"
          />
          {compareEnabled && (
            <Typography variant="caption" color="textSecondary" sx={{ ml: 4, display: 'block' }}>
              ⏱️ Vergelijken vereist extra data en kan langer duren
            </Typography>
          )}
        </Box>
        
        {/* Current Period */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" color="primary">
              {compareEnabled ? 'Huidige Periode:' : 'Periode:'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Van datum"
              value={dateFrom}
              onChange={(newValue) => setDateFrom(newValue)}
              format="DD-MM-YYYY"
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Tot datum"
              value={dateTo}
              onChange={(newValue) => setDateTo(newValue)}
              format="DD-MM-YYYY"
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
        </Grid>

        {/* Compare Period - Collapsible */}
        <Collapse in={compareEnabled}>
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom color="primary">
              Vergelijkperiode
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Vergelijk van"
                  value={compareDateFrom}
                  onChange={(newValue) => setCompareDateFrom(newValue)}
                  format="DD-MM-YYYY"
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Vergelijk tot"
                  value={compareDateTo}
                  onChange={(newValue) => setCompareDateTo(newValue)}
                  format="DD-MM-YYYY"
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const days = dateTo.diff(dateFrom, 'day');
                    setCompareDateFrom(dateFrom.subtract(days + 1, 'day'));
                    setCompareDateTo(dateFrom.subtract(1, 'day'));
                  }}
                >
                  Vorige periode
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setCompareDateFrom(dateFrom.subtract(1, 'year'));
                    setCompareDateTo(dateTo.subtract(1, 'year'));
                  }}
                >
                  Vorig jaar
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Controls */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Zoeken"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max producten"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
              inputProps={{ min: 10, max: 200 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Tooltip title={compareEnabled 
              ? "Vergelijk producten voor de geselecteerde periodes - gebruik na het wijzigen van datums"
              : "Analyseer productverkopen voor de geselecteerde periode"
            }>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => loadProductComparison(false)}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CompareArrowsIcon />}
              >
                {loading ? 'Laden...' : (compareEnabled ? 'Vergelijken' : 'Analyseren')}
              </Button>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={2}>
            <Tooltip title={compareEnabled 
              ? "Ververs de vergelijking voor de huidige periodes - haalt nieuwste gegevens op"
              : "Ververs de productverkopen voor de huidige periode - haalt nieuwste gegevens op"
            }>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={refreshProductComparison}
                disabled={loading}
                startIcon={<RefreshIcon />}
              >
                Vernieuwen
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {(compareEnabled ? productComparison.length > 0 : productSales.length > 0) && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {compareEnabled ? 'Analytics Vergelijking' : 'Analytics Overzicht'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {filteredProducts.length} van {compareEnabled ? productComparison.length : productSales.length} producten
              </Typography>
            </Box>

            <Paper sx={{ overflow: 'auto', maxHeight: 800 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="center">SKU</TableCell>
                    {compareEnabled ? (
                      <>
                        <TableCell align="center">Verkocht (Huidig)</TableCell>
                        <TableCell align="center">Verkocht (Vergelijk)</TableCell>
                        <TableCell align="center">Trend Aantal</TableCell>
                        <TableCell align="center">Omzet (Huidig)</TableCell>
                        <TableCell align="center">Omzet (Vergelijk)</TableCell>
                        <TableCell align="center">Trend Omzet</TableCell>
                        <TableCell align="center">Orders (Huidig)</TableCell>
                        <TableCell align="center">Orders (Vergelijk)</TableCell>
                        <TableCell align="center">Trend Orders</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="center">Verkocht</TableCell>
                        <TableCell align="center">Omzet</TableCell>
                        <TableCell align="center">Orders</TableCell>
                        <TableCell align="center">Gem. Prijs</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={`${product.productId}-${product.sku || 'no-sku'}`} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold" noWrap>
                            {product.productName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {product.productId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={product.sku || 'N/A'} size="small" variant="outlined" />
                      </TableCell>
                      
                      {compareEnabled ? (
                        <>
                          {/* Comparison Mode - Quantity */}
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {product.current.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {product.compare.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`${formatPercentage(product.changes.quantity)} verandering`}>
                              <Chip
                                icon={getPercentageIcon(product.changes.quantity)}
                                label={formatPercentage(product.changes.quantity)}
                                color={getPercentageColor(product.changes.quantity)}
                                size="small"
                              />
                            </Tooltip>
                          </TableCell>

                          {/* Comparison Mode - Revenue */}
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(product.current.revenue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {formatCurrency(product.compare.revenue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`${formatPercentage(product.changes.revenue)} verandering`}>
                              <Chip
                                icon={getPercentageIcon(product.changes.revenue)}
                                label={formatPercentage(product.changes.revenue)}
                                color={getPercentageColor(product.changes.revenue)}
                                size="small"
                              />
                            </Tooltip>
                          </TableCell>



                          {/* Comparison Mode - Orders */}
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {product.current.orders}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {product.compare.orders}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`${formatPercentage(product.changes.orders)} verandering`}>
                              <Chip
                                icon={getPercentageIcon(product.changes.orders)}
                                label={formatPercentage(product.changes.orders)}
                                color={getPercentageColor(product.changes.orders)}
                                size="small"
                              />
                            </Tooltip>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          {/* Single Period Mode */}
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {product.totalQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(product.totalRevenue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {product.orderCount}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {formatCurrency(product.averagePrice)}
                            </Typography>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </CardContent>
        </Card>
      )}

      {!loading && (compareEnabled ? productComparison.length === 0 : productSales.length === 0) && (
        <Alert severity="info" sx={{ mt: 3 }}>
          {compareEnabled 
            ? 'Geen productgegevens gevonden voor de geselecteerde periodes. Probeer andere datums of zorg ervoor dat de database gesynchroniseerd is.'
            : 'Geen productverkopen gevonden voor de geselecteerde periode. Probeer andere datums of zorg ervoor dat de database gesynchroniseerd is.'
          }
        </Alert>
      )}
    </Box>
  );
};

export default ProductComparison; 